/* LumaSpec RFQ forms (snippets/ls-rfq-form.liquid; QUOTE lane). One sender for the quote page, the home page section
   and the product page's quick-quote dialog. Each request goes:
   1. as JSON to the app proxy /apps/quote (src/invoices/worker/quote.mjs): a Shopify draft order tagged quote-request,
      answered {ok, ref} or {ok: false, error[, no_us]}; src/rfq_mail.py (task "LumaSpec RFQ Mail", every 2 minutes)
      files it in the support@lumaspecvision.com inbox with Reply-To = the customer (IMD's morning worklist: OWES_REPLY);
      src/quotes.py prices and sends it. Then straight to the thank-you (no captcha, no ad-blocker trouble);
   2. only when no draft order was saved (server not reachable / failed): the form itself, which is Shopify's contact
      form - it emails support@ from mailer@shopify.com with Reply-To = the customer and every item (worklist: FORM).
   A request the server refuses (bad input, not sold in the US, flood) stops with its message. The thank-you shows on
   the quote page (?rfq=sent) from sessionStorage.
   Items: page = the quote list (window.LSQuote; ls-cfg-quote.js adds hand-offs and US checks through form.lsRfq),
   home = the quote list when it has items, product = this product ([data-ls-quote-item] of the buy box) x quantity. */

const QKEY = 'lumaspec-quote-v1';
const DONE_KEY = 'lumaspec-rfq-done';
const SITE = 'https://lumaspecvision.com';

const DEFAULTS = {
  err_name: 'Please enter your name.',
  err_email: 'Please enter a valid email address.',
  err_country: 'Please choose the delivery country.',
  err_need: 'Tell us what you need: part numbers, quantities or your application.',
  err_generic: 'Sorry, something went wrong. Please email support@lumaspecvision.com.',
  sending: 'Sending...',
  list_one: 'Your quote list ({n} item) is included.',
  list_other: 'Your quote list ({n} items) is included.',
  done_title: 'Thank you - your request is in',
  done_ref_html: 'Reference <b>{ref}</b>.',
  done_text_html: 'We will email your quote to <b>{email}</b>, usually within one business day.',
  done_next: 'Continue browsing',
};

/** @param {HTMLFormElement} form */
function strings(form) {
  const out = { ...DEFAULTS };
  const el = document.querySelector(`[data-ls-rfq-i18n="${form.id}"]`) || document.querySelector('[data-ls-rfq-i18n]');
  let d = {};
  try {
    d = JSON.parse(el?.textContent || '{}') || {};
  } catch {
    d = {};
  }
  const dec = document.createElement('textarea'); // the t filter HTML-escapes plain keys: back to plain text once
  for (const [k, v] of Object.entries(d)) {
    if (typeof v !== 'string' || !v) continue;
    if (k.endsWith('_html')) out[k] = v;
    else {
      dec.innerHTML = v;
      out[k] = dec.value;
    }
  }
  return out;
}
const fill = (s, vars) => String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const quote = {
  list() {
    if (window.LSQuote) return window.LSQuote.list();
    try {
      return JSON.parse(localStorage.getItem(QKEY) || '[]') || [];
    } catch {
      return [];
    }
  },
  clear() {
    if (window.LSQuote) return window.LSQuote.save([]);
    try {
      localStorage.setItem(QKEY, '[]');
    } catch {
      /* storage blocked */
    }
  },
};

const itemUrl = (i) => i.u || (/^lumaspec-/.test(i.h || '') ? `${location.origin}/products/${i.h}` : `${SITE}/p/${i.h}/`);
const optText = (o) => (o && typeof o === 'object' ? Object.entries(o).map(([k, v]) => `${k}: ${v}`).join(', ') : o ? String(o) : '');

/** The items this form sends. @param {HTMLFormElement} form */
function itemsOf(form) {
  const mode = form.dataset.lsRfq;
  if (form.lsRfq?.items) return form.lsRfq.items();
  if (mode === 'product') {
    const pdp = form.closest('[data-ls-pdp]') || document.querySelector('[data-ls-pdp]');
    let it = null;
    try {
      it = JSON.parse(pdp?.querySelector('[data-ls-quote-item]')?.textContent || 'null');
    } catch {
      it = null;
    }
    if (!it) return [];
    const q = Math.max(1, Math.min(100000, parseInt(field(form, 'qty')?.value || '1', 10) || 1));
    const item = { v: String(it.v || ''), sku: it.sku || '', t: it.t, h: it.h, i: it.i || '', q };
    if (it.o) item.o = it.o;
    return [item];
  }
  return quote.list();
}

/** @param {HTMLFormElement} form @param {string} f */
const field = (form, f) => /** @type {HTMLInputElement|null} */ (form.querySelector(`[data-f="${f}"]`));
const val = (form, f) => String(field(form, f)?.value || '').trim();

/** Preserve a configured quote page's existing query and fragment when adding the confirmation state. */
function thankYouUrl(form) {
  const value = form.dataset.quoteUrl || '/pages/quote';
  const url = new URL(value, location.href);
  url.searchParams.set('rfq', 'sent');
  return value.startsWith('/') && !value.startsWith('//') ? url.pathname + url.search + url.hash : url.href;
}

function isRfqConfirmation(value) {
  try {
    const query = new URL(value, location.href).searchParams;
    return query.get('rfq') === 'sent' || query.get('contact_posted') === 'true';
  } catch {
    return true;
  }
}

function summary(ref, items, b) {
  const lines = [
    `LumaSpec Vision RFQ${ref ? ' ' + ref : ''} - lumaspecvision.com ${b.source || b.mode}${ref ? ' (draft order in the LumaSpec store)' : ' (not saved as a draft order: quote from this email)'}`,
  ];
  if (items.length) {
    lines.push(`Items (${items.length}):`);
    for (const i of items) {
      const o = optText(i.o);
      lines.push(`- ${i.q || 1} x ${i.sku || ''} ${i.t || ''}${o ? ' [' + o + ']' : ''} ${itemUrl(i)}`.replace(/\s+/g, ' '));
    }
  }
  if (b.config) lines.push(`Configuration: ${b.config}`);
  lines.push(`Sent from: ${b.page}`);
  return lines.join('\n').slice(0, 4800);
}

/** @param {HTMLFormElement} form */
function init(form) {
  if (form.dataset.lsRfqReady) return;
  form.dataset.lsRfqReady = '1';
  form.noValidate = true; // our own messages; without JavaScript the browser's required checks apply
  const S = strings(form);
  const err = form.querySelector('[data-rfq-err]');
  const btn = form.querySelector('button[type=submit]');
  const label = form.querySelector('[data-rfq-btn]');
  const opened = Date.now();

  // home: say so when the visitor's quote list goes with the request
  const note = form.querySelector('[data-rfq-listnote]');
  const paintNote = () => {
    if (!note) return;
    const n = quote.list().length;
    note.hidden = !n;
    const t = note.querySelector('[data-rfq-listtext]');
    if (t) t.textContent = fill(n === 1 ? S.list_one : S.list_other, { n });
  };
  paintNote();
  document.addEventListener('ls:quote-change', paintNote);

  const fail = (msg, f) => {
    if (err) {
      err.textContent = msg;
      err.hidden = false;
    }
    for (const el of form.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
    const el = f ? field(form, f) : null;
    if (el) {
      el.setAttribute('aria-invalid', 'true');
      el.focus();
    } else err?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
  const busy = (on) => {
    if (btn) btn.disabled = on;
    if (label) {
      if (on) label.dataset.text = label.textContent || '';
      label.textContent = on ? S.sending : label.dataset.text || label.textContent;
    }
  };

  // On window, capture phase: ahead of Shopify's own contact-form bot check (storefront-forms-hcaptcha), which otherwise
  // takes the same submit, shows its captcha and posts the bare form natively when it resolves - racing our request
  // (2026-10-26: one RFQ arrived as an empty contact email with no draft order, another as draft order + duplicate email)
  window.addEventListener('submit', async (ev) => {
    if (ev.target !== form) return;
    if (form.dataset.lsMail === '1') return; // second pass: Shopify's contact form posts (and emails us)
    ev.preventDefault();
    ev.stopImmediatePropagation();
    if (err) err.hidden = true;
    const ext = form.lsRfq || {};
    const items = itemsOf(form);
    const b = {
      t: opened, page: location.href, items, vc: form.dataset.country || '', mode: form.dataset.lsRfq,
      name: val(form, 'name'), email: val(form, 'email'), company: val(form, 'company'), phone: val(form, 'phone'),
      country: val(form, 'country').toUpperCase(), zip: val(form, 'zip'), needed: val(form, 'needed'),
      application: val(form, 'need'), notes: val(form, 'notes'),
      website: String(/** @type {HTMLInputElement|null} */ (form.querySelector('input[name=website]'))?.value || ''),
      ...(ext.ctx ? ext.ctx() : {}),
    };
    if (!b.source) b.source = form.dataset.lsRfq === 'product' ? 'product' : form.dataset.lsRfq === 'home' ? 'home' : '';
    if (!b.name) return fail(S.err_name, 'name');
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(b.email)) return fail(S.err_email, 'email');
    if (!/^[A-Z]{2}$/.test(b.country)) return fail(S.err_country, 'country');
    if (!items.length && !b.application && !b.notes) return fail(S.err_need, 'need');
    const pre = ext.check ? ext.check(b) : '';
    if (pre) return fail(pre);

    busy(true);
    let ref = '';
    try {
      const r = await fetch(form.dataset.endpoint || '/apps/quote', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(b),
      });
      const d = await r.json().catch(() => ({ ok: false }));
      if (d.ok) ref = d.ref || '';
      else if ([400, 413, 429].includes(r.status)) { // refused on purpose: say why, send nothing
        ext.refused?.(d);
        busy(false);
        const en = /^en\b/i.test(document.documentElement.lang || 'en'); // the Worker's own texts are English
        if (d.field === 'email') return fail((en && d.error) || S.err_email, 'email'); // e.g. a mistyped domain
        return fail((d.no_us && ext.noUsMessage) || (en && d.error) || S.err_generic);
      }
    } catch {
      /* not reachable: the email below still carries the whole request */
    }

    const listed = form.dataset.lsRfq !== 'product' && items.length > 0;
    const sum = form.querySelector('[data-rfq-summary]');
    if (sum) sum.value = summary(ref, items, b);
    try {
      sessionStorage.setItem(DONE_KEY, JSON.stringify({ ref, email: b.email, at: Date.now(), back: location.pathname + location.search + location.hash, clear: !ref && listed }));
    } catch {
      /* storage blocked: the page shows Shopify's own "sent" line */
    }
    if (ref && listed) quote.clear(); // saved as a draft order; otherwise the list goes once the email is out (done())
    ext.sent?.();
    try {
      window.Shopify?.analytics?.publish?.('ls_quote_request', { lead_source: b.source || 'quote', items: items.length });
    } catch {
      /* analytics optional */
    }
    const thanks = thankYouUrl(form);
    // saved as a draft order: src/rfq_mail.py files it in the support inbox (Reply-To = customer) within minutes
    if (ref) return location.assign(thanks);
    let rt = form.querySelector('input[name=return_to]');
    if (!rt) {
      rt = document.createElement('input');
      rt.type = 'hidden';
      rt.name = 'return_to';
      form.append(rt);
    }
    /** @type {HTMLInputElement} */ (rt).value = thanks;
    form.dataset.lsMail = '1';
    // Shopify's bot check (hCaptcha) may show a challenge: a modal dialog would cover it (top layer), so close it first
    const dlg = form.closest('dialog');
    if (dlg?.open) dlg.close();
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
    // a challenge left unanswered (or blocked): let them retry
    setTimeout(() => {
      form.dataset.lsMail = '';
      busy(false);
    }, 45000);
  }, true);
}

/* After the post: the thank-you on the quote page; any other page Shopify returned to forwards there. */
function done() {
  const q = new URLSearchParams(location.search);
  if (q.get('rfq') !== 'sent' && q.get('contact_posted') !== 'true') return;
  let p = null;
  try {
    p = JSON.parse(sessionStorage.getItem(DONE_KEY) || 'null');
  } catch {
    p = null;
  }
  const box = document.querySelector('[data-ls-rfq-done]');
  if (!box) {
    const form = document.querySelector('form[data-ls-rfq]');
    if (p && form instanceof HTMLFormElement) location.replace(thankYouUrl(form));
    else form?.closest('dialog')?.showModal?.(); // storage blocked: Shopify's "sent" line inside the form
    return;
  }
  if (!p && q.get('rfq') !== 'sent') return; // a plain contact-form post (no JavaScript): the form shows Shopify's line
  const form = document.querySelector('form[data-ls-rfq]');
  const S = form instanceof HTMLFormElement ? strings(form) : DEFAULTS;
  for (const el of document.querySelectorAll('[data-ls-rfq-hide]')) /** @type {HTMLElement} */ (el).hidden = true;
  const localeRoot = window.Shopify?.routes?.root || '/';
  const back = p?.back && !isRfqConfirmation(p.back) ? p.back : `${localeRoot}collections/all`;
  box.innerHTML =
    '<span class="ls-qp__ok"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg></span>' +
    `<div><h2 class="ls-h3">${esc(S.done_title)}</h2><p>${p?.ref ? fill(S.done_ref_html, { ref: esc(p.ref) }) + ' ' : ''}` +
    `${p?.email ? fill(S.done_text_html, { email: esc(p.email) }) : ''}</p>` +
    `<p><a class="ls-btn ls-btn--outline ls-btn--sm" href="${esc(back)}">${esc(S.done_next)}</a></p></div>`;
  box.hidden = false;
  box.focus();
  if (p?.clear) quote.clear();
  try {
    sessionStorage.removeItem(DONE_KEY);
  } catch {
    /* */
  }
  q.delete('rfq');
  q.delete('contact_posted');
  const query = q.toString();
  history.replaceState(null, '', location.pathname + (query ? `?${query}` : '') + location.hash);
}

const run = () => {
  for (const f of document.querySelectorAll('form[data-ls-rfq]')) init(/** @type {HTMLFormElement} */ (f));
};
run();
done();
document.addEventListener('shopify:section:load', run);

/* Product page: the quick-quote dialog ([data-ls-rfq-open] buttons -> dialog[data-ls-rfq-dialog]) */
document.addEventListener('click', (ev) => {
  const t = ev.target instanceof Element ? ev.target : null;
  const open = t?.closest('[data-ls-rfq-open]');
  if (open) {
    const dlg = document.getElementById(open.getAttribute('aria-controls') || '');
    if (!(dlg instanceof HTMLDialogElement)) return;
    // the quantity picked on the page goes into the dialog
    const pdp = open.closest('[data-ls-pdp]');
    const qEl = pdp?.querySelector('[data-ls-qty]') || pdp?.querySelector('input[name="quantity"]');
    const qd = dlg.querySelector('[data-f="qty"]');
    if (qEl && qd && 'value' in qEl) /** @type {HTMLInputElement} */ (qd).value = String(qEl.value || '1');
    // the selected option shows in the dialog's product line
    const it = pdp?.querySelector('[data-ls-quote-item]');
    const line = dlg.querySelector('[data-rfq-opts]');
    if (it && line) {
      try {
        line.textContent = optText(JSON.parse(it.textContent || 'null')?.o);
      } catch {
        /* keep */
      }
    }
    dlg.showModal();
    /** @type {HTMLElement|null} */ (dlg.querySelector('[data-f="qty"], [data-f="name"]'))?.focus();
    return;
  }
  const close = t?.closest('[data-ls-rfq-close]');
  if (close) close.closest('dialog')?.close();
  else if (t instanceof HTMLDialogElement && t.matches('[data-ls-rfq-dialog]')) t.close(); // backdrop click
});
